
import React, { memo } from "react";
import { ReactFlow, MiniMap, Controls, Background, Node, Edge, ConnectionLineType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import FlowNodeComponent from "../FlowNode";
import ErrorBoundary from "../ErrorBoundary";

const nodeTypes = {
  customNode: FlowNodeComponent,
};

interface WhiteboardCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: any) => void;
  onEdgesChange: (changes: any) => void;
  onConnect: (connection: any) => void;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
}

const WhiteboardCanvas: React.FC<WhiteboardCanvasProps> = memo(({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onEdgeClick
}) => {
  return (
    <div className="flex-1 h-full w-full overflow-hidden">
      <ErrorBoundary fallback={
        <div className="h-full w-full flex items-center justify-center">
          <div className="text-red-500">ReactFlow error. Please refresh.</div>
        </div>
      }>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          nodeTypes={nodeTypes}
          fitView
          className="w-full h-full reference-whiteboard"
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          aria-label="Interactive whiteboard canvas"
          proOptions={{ hideAttribution: true }}
          nodesDraggable={true}
          nodesConnectable={true}
          elementsSelectable={true}
          selectNodesOnDrag={false}
          panOnDrag={[1, 2]}
          zoomOnScroll={true}
          zoomOnPinch={true}
          deleteKeyCode={null}
          multiSelectionKeyCode="Control"
          nodeOrigin={[0.5, 0.5]}
          snapToGrid={false}
          snapGrid={[15, 15]}
          connectionLineType={ConnectionLineType.SmoothStep}
          minZoom={0.1}
          maxZoom={4}
          translateExtent={[[-2000, -2000], [2000, 2000]]}
          nodeExtent={[[-2000, -2000], [2000, 2000]]}
          style={{
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
          }}
        >
          <Controls 
            position="bottom-right" 
            className="reference-controls"
            showInteractive={false}
          />
          <MiniMap 
            position="top-left" 
            className="reference-minimap"
            pannable
            zoomable
            style={{ backgroundColor: 'rgba(245, 247, 250, 0.9)' }}
          />
          <Background 
            gap={20} 
            size={1} 
            color="rgba(255, 255, 255, 0.3)"
            style={{ backgroundColor: 'transparent' }}
          />
        </ReactFlow>
      </ErrorBoundary>
    </div>
  );
});

WhiteboardCanvas.displayName = 'WhiteboardCanvas';

export default WhiteboardCanvas;
